import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "@tanstack/react-router";
import { useAuth } from "@project/hooks/useAuth";
import { toast } from "sonner";
import {
  ArrowRight,
  Check,
  Heart,
  Home,
  Hammer,
  Leaf,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkles,
  Sprout,
  Stethoscope,
} from "lucide-react";
import { PageShell } from "@project/user/components/page-shell";
import {
  CountUp,
  ImageReveal,
  Parallax,
  Reveal,
  RevealText,
  Stagger,
  StaggerItem,
} from "@project/user/components/motion";
import { GaushalaSponsorshipModal } from "@project/user/components/gaushala-sponsorship-modal";
import { GaushalaBankDetails } from "@project/user/components/gaushala-bank-details";
import { Button } from "@project/components/ui/button";
import { useI18n } from "@project/lib/use-i18n";
import { publicGaushalaService } from "@project/services/donate/gaushala.service";
import {
  GAUSHALA_SPONSORSHIP_PROGRAMS,
  formatGaushalaPrice,
  type GaushalaProgram,
  type GaushalaScheme,
  type GaushalaSponsorshipSelection,
} from "@project/types/gaushala";

import { galleryCategoryService } from "@project/services/admin/gallery-category.service";
import { galleryService } from "@project/services/admin/gallery.service";
import { resolveMediaUrl } from "@project/lib/utils";

import gaushalaImg1 from "@project/assets/Gaushala/image (1).jfif";
import gaushalaImg2 from "@project/assets/Gaushala/image (2).jfif";
import gaushalaImg3 from "@project/assets/Gaushala/image (3).jfif";

/** Shared hover: soft lift & clean border */
const cardHoverClass =
  "border border-border/80 bg-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/50 hover:shadow-lg rounded-2xl p-6 flex flex-col justify-between h-full";

export interface GaushalaImpactStats {
  cowsSheltered: number;
  dailyMeals: number;
  medicalCarePercentage: number;
  yearsOfService: number;
}

const DEFAULT_IMPACT_STATS: GaushalaImpactStats = {
  cowsSheltered: 210,
  dailyMeals: 1200,
  medicalCarePercentage: 100,
  yearsOfService: 9,
};

/** Plan card for Gaushala Sponsorship options */
function SponsorshipPlanCard({
  program,
  sponsorLabel,
  onSponsor,
}: {
  program: GaushalaProgram & { button_text?: string };
  sponsorLabel: string;
  onSponsor: () => void;
}) {
  const highlighted = program.is_highlighted === true;

  return (
    <article
      className={`relative flex h-full flex-col justify-between rounded-2xl bg-card p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 ${highlighted
        ? "border-2 border-[#E38018] shadow-md hover:shadow-xl"
        : "border-border/80 hover:border-primary/50 border hover:shadow-lg"
        }`}
    >
      <div>
        <div className="flex items-center justify-between gap-2">
          <span
            className={`w-fit rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${highlighted
              ? "bg-[#fff1e0] text-[#E38018]"
              : "bg-primary/10 text-primary"
              }`}
          >
            {program.badge ?? "Gau Seva"}
          </span>
          {program.period && (
            <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
              {program.period}
            </span>
          )}
        </div>

        <h3 className="mt-4 !font-sans !text-3xl !font-semibold leading-snug text-foreground">
          {program.name}
        </h3>

        <div className="mt-3 flex items-baseline gap-1">
          <span
            className={`font-sans text-3xl font-bold tracking-tight ${highlighted ? "text-[#E38018]" : "text-primary"
              }`}
          >
            {formatGaushalaPrice(program.price)}
          </span>
        </div>

        {program.description && (
          <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
            {program.description}
          </p>
        )}

        <div className="border-border/60 my-5 border-t" />

        {program.features && program.features.length > 0 && (
          <ul className="space-y-2.5">
            {program.features.map(feature => (
              <li
                key={`${feature}`}
                className="text-foreground/85 flex items-start gap-2.5 text-xs font-medium"
              >
                <Check
                  className={`mt-0.5 h-4 w-4 shrink-0 ${highlighted ? "text-[#E38018]" : "text-emerald-600"
                    }`}
                  strokeWidth={2.5}
                />
                <span className="leading-snug">{feature}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Button
        type="button"
        onClick={onSponsor}
        className={`mt-6 h-11 w-full rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm ${highlighted
          ? "bg-[#E38018] text-white hover:bg-[#E38018]/90"
          : "hover:bg-primary/90 bg-primary text-primary-foreground"
          }`}
      >
        {program.button_text ?? sponsorLabel}
      </Button>
    </article>
  );
}

/** Landscape banner card for Infrastructure & Development Fund */
function InfrastructureBannerCard({
  program,
  sponsorLabel,
  onSponsor,
}: {
  program: GaushalaProgram;
  sponsorLabel: string;
  onSponsor: () => void;
}) {
  return (
    <article className="border-primary/20 hover:border-primary/40 overflow-hidden rounded-2xl border bg-card shadow-sm transition-all duration-300 hover:shadow-md">
      <div className="grid gap-6 p-6 sm:p-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
        <div>
          <span className="bg-primary/10 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">
            <Hammer className="h-3 w-3" />
            {program.badge ?? "Infrastructure"}
          </span>
          <h3 className="mt-3 font-serif text-2xl font-bold text-foreground">
            {program.name}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {program.description}
          </p>

          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {program.features.map(feature => (
              <div
                key={feature}
                className="bg-secondary/30 text-foreground/90 flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs font-medium"
              >
                <Check
                  className="h-3.5 w-3.5 shrink-0 text-primary"
                  strokeWidth={2.5}
                />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-secondary/20 flex flex-col justify-center rounded-xl border border-border p-6 text-center shadow-sm">
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Development Contribution
          </span>
          <div className="mt-2 font-sans text-3xl font-bold text-primary">
            {formatGaushalaPrice(program.price)}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {program.period ?? "One-time donation"}
          </p>
          <Button
            type="button"
            onClick={onSponsor}
            className="hover:bg-primary/90 mt-5 h-11 w-full rounded-xl bg-primary text-xs font-bold uppercase tracking-wider text-primary-foreground"
          >
            {sponsorLabel}
          </Button>
        </div>
      </div>
    </article>
  );
}

export default function GaushalaPage({
  impactStats = DEFAULT_IMPACT_STATS,
}: {
  impactStats?: GaushalaImpactStats;
}) {
  const { t } = useI18n();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [selection, setSelection] =
    useState<GaushalaSponsorshipSelection | null>(null);

  const [apiSchemes, setApiSchemes] = useState<GaushalaScheme[]>([]);
  const [dynamicGallery, setDynamicGallery] = useState<{ src: string; title: string; desc?: string }[]>([]);

  useEffect(() => {
    let ignore = false;
    publicGaushalaService
      .listActiveSchemes()
      .then(res => {
        if (!ignore && res.data?.length) {
          setApiSchemes(res.data);
        }
      })
      .catch(() => { });

    const loadGaushalaGallery = async () => {
      try {
        const catRes = await galleryCategoryService.listPage({ page: 1, page_size: 50 });
        const categories = (catRes.data || []).filter(c => c.active !== false);
        const gauCat = categories.find(c =>
          c.category_name?.toLowerCase().includes("gaushala") ||
          c.category_name?.toLowerCase().includes("gau") ||
          c.category_name?.toLowerCase().includes("cow")
        ) || categories[0];

        if (gauCat) {
          const itemRes = await galleryService.listPage({
            category: gauCat.id,
            page: 1,
            page_size: 10,
          });
          const activeItems = (itemRes.data || []).filter(i => i.active !== false).slice(0, 3);
          if (!ignore && activeItems.length > 0) {
            const mapped = activeItems
              .map(item => ({
                src: resolveMediaUrl(item.image_url || item.image) || "",
                title: item.title || gauCat.category_name || "Gaushala Seva",
                desc: item.description || "",
              }))
              .filter(i => !!i.src);
            if (mapped.length > 0) {
              setDynamicGallery(mapped);
            }
          }
        }
      } catch (err) {
        console.error("Failed to load dynamic gaushala gallery", err);
      }
    };
    loadGaushalaGallery();

    return () => {
      ignore = true;
    };
  }, []);

  const displayPrograms = useMemo<
    (GaushalaProgram & { button_text?: string })[]
  >(() => {
    if (apiSchemes.length > 0) {
      return apiSchemes.map(s => {
        const stdMatch = GAUSHALA_SPONSORSHIP_PROGRAMS.find(
          p =>
            p.name.toLowerCase() === s.title.toLowerCase() ||
            String(p.id) === String(s.id)
        );
        const rawFeatures = Array.isArray(s.features)
          ? s.features.map(f => String(f).trim()).filter(Boolean)
          : [];
        const features =
          rawFeatures.length > 0
            ? rawFeatures
            : (stdMatch?.features ?? [
              "Fresh green fodder & clover mix",
              "Veterinary checkups & medical protection",
              "Safe shelter and clean drinking water",
              "80G tax exemption receipt provided",
            ]);
        const periodLabel =
          s.duration && s.duration_type
            ? `${s.duration} ${s.duration_type.toUpperCase()}`
            : (stdMatch?.period ?? "");

        return {
          id: s.id,
          name: s.title,
          badge:
            s.short_title ??
            stdMatch?.badge ??
            (s.is_highlighted ? "Featured Care" : "Gau Seva"),
          description: s.description ?? stdMatch?.description ?? "",
          price: String(s.amount),
          period: periodLabel,
          is_highlighted: Boolean(s.is_highlighted),
          button_text: s.button_text ?? "SPONSOR NOW",
          features,
        };
      });
    }
    return GAUSHALA_SPONSORSHIP_PROGRAMS;
  }, [apiSchemes]);

  const openSponsorship = (program: GaushalaProgram) => {
    if (!isAuthenticated) {
      toast.info("Please log in to continue with Gaushala sponsorship");
      navigate({
        to: "/login",
        search: { redirectTo: "/gaushala" },
      });
      return;
    }
    setSelection({
      kind: "program",
      id: program.id,
      title: program.name,
      description: program.description,
      price: program.price,
      categoryLabel: t("programs.gaushala.sponsorships.eyebrow"),
      badgeText: program.badge,
      period: program.period,
    });
    setModalOpen(true);
  };

  const scrollToSponsorships = () => {
    document
      .getElementById("gaushala-sponsorships")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const whyUsCards = [
    {
      icon: Sprout,
      titleKey: "programs.gaushala.why.card1Title",
      descKey: "programs.gaushala.why.card1Desc",
    },
    {
      icon: Stethoscope,
      titleKey: "programs.gaushala.why.card2Title",
      descKey: "programs.gaushala.why.card2Desc",
    },
    {
      icon: Home,
      titleKey: "programs.gaushala.why.card3Title",
      descKey: "programs.gaushala.why.card3Desc",
    },
    {
      icon: Sparkles,
      titleKey: "programs.gaushala.why.card4Title",
      descKey: "programs.gaushala.why.card4Desc",
    },
    {
      icon: ShieldCheck,
      titleKey: "programs.gaushala.why.card5Title",
      descKey: "programs.gaushala.why.card5Desc",
    },
    {
      icon: Heart,
      titleKey: "programs.gaushala.why.card6Title",
      descKey: "programs.gaushala.why.card6Desc",
    },
  ];

  const galleryItems = [
    {
      src: gaushalaImg1,
      titleKey: "programs.gaushala.gallery.item1Title",
      descKey: "programs.gaushala.gallery.item1Desc",
    },
    {
      src: gaushalaImg2,
      titleKey: "programs.gaushala.gallery.item3Title",
      descKey: "programs.gaushala.gallery.item3Desc",
    },
    {
      src: gaushalaImg3,
      titleKey: "programs.gaushala.gallery.item6Title",
      descKey: "programs.gaushala.gallery.item6Desc",
    },
  ];

  const mainPrograms = displayPrograms.slice(0, 5);
  const infrastructureProgram =
    displayPrograms[5] || GAUSHALA_SPONSORSHIP_PROGRAMS[5];

  return (
    <PageShell>
      {/* 1. Hero Banner */}
      <section className="bg-hero-gradient relative overflow-hidden text-primary-foreground shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18)]">
        <Parallax speed={0.06} className="anim-fade absolute inset-0">
          <img
            src={gaushalaImg1}
            alt=""
            aria-hidden
            loading="eager"
            className="h-full w-full object-cover opacity-15"
          />
        </Parallax>
        <div className="from-primary/60 via-primary/45 to-gold/35 absolute inset-0 bg-gradient-to-br" />
        <div className="from-primary/50 to-primary/25 absolute inset-0 bg-gradient-to-t via-transparent" />

        <div className="relative mx-auto grid max-w-site items-center gap-10 px-4 py-12 sm:py-14 md:grid-cols-2 md:gap-12 md:py-16 lg:gap-16">
          <div className="text-left">
            <span
              className="bg-gold/25 border-gold/40 anim-fade-up inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-gold"
              style={{ animationDelay: "120ms" }}
            >
              <Leaf className="h-3.5 w-3.5" />
              {t("programs.gaushala.hero.badge")}
            </span>

            <h1 className="mt-5 font-serif text-4xl font-bold leading-tight drop-shadow sm:text-5xl md:text-[3.25rem]">
              <span className="anim-line" style={{ animationDelay: "240ms" }}>
                <span>{t("programs.gaushala.hero.brand")}</span>
              </span>
            </h1>

            <p
              className="anim-fade-up mt-4 text-base font-semibold leading-snug text-gold sm:text-lg"
              style={{ animationDelay: "400ms" }}
            >
              {t("programs.gaushala.hero.title")}
            </p>

            <div
              className="text-primary-foreground/95 anim-fade-up mt-5 max-w-xl space-y-2.5 text-sm sm:text-base leading-relaxed"
              style={{ animationDelay: "520ms" }}
            >
              <div className="flex items-start gap-2.5">
                <MapPin className="mt-1 h-4 w-4 shrink-0 text-gold" />
                <p>
                  <strong className="font-semibold text-white">
                    {t("programs.gaushala.hero.addressLabel", { defaultValue: "Address" })}:{" "}
                  </strong>
                  <span className="text-white/90">
                    {t("programs.gaushala.hero.address")}
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 shrink-0 text-gold" />
                <p>
                  <strong className="font-semibold text-white">
                    {t("programs.gaushala.hero.phoneLabel", { defaultValue: "Mo" })}:{" "}
                  </strong>
                  <span className="text-white/90">
                    <a
                      href="tel:+919978015424"
                      className="transition-colors hover:text-gold hover:underline"
                    >
                      {t("programs.gaushala.hero.phone1", { defaultValue: "99780-15424" })}
                    </a>
                    {", "}
                    <a
                      href="tel:+919099930582"
                      className="transition-colors hover:text-gold hover:underline"
                    >
                      {t("programs.gaushala.hero.phone2", { defaultValue: "90999-30582" })}
                    </a>
                  </span>
                </p>
              </div>
            </div>

            <div
              className="anim-fade-up mt-8 flex flex-wrap gap-3"
              style={{ animationDelay: "660ms" }}
            >
              <Button
                type="button"
                onClick={scrollToSponsorships}
                className="hover:bg-gold/90 btn-lift h-11 gap-2 rounded-xl bg-gold px-5 font-semibold text-gold-foreground"
              >
                <Heart className="h-4 w-4 fill-current" />
                {t("programs.gaushala.hero.primaryCta")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  openSponsorship(
                    mainPrograms[1] || GAUSHALA_SPONSORSHIP_PROGRAMS[1]
                  )
                }
                className="border-primary-foreground/50 hover:bg-primary-foreground/10 btn-lift h-11 gap-2 rounded-xl bg-transparent px-5 font-semibold text-primary-foreground hover:text-primary-foreground"
              >
                <ArrowRight className="h-4 w-4" />
                {t("programs.gaushala.hero.secondaryCta")}
              </Button>
            </div>
          </div>

          <div className="relative flex justify-center md:justify-end">
            <img
              src={gaushalaImg1}
              alt={t("programs.gaushala.hero.imageAlt")}
              style={{ animationDelay: "380ms" }}
              className="anim-iris aspect-[4/3] max-h-60 w-full max-w-sm rounded-2xl object-cover shadow-[0_20px_50px_-20px_rgba(0,0,0,0.55)] ring-1 ring-white/20 sm:max-h-72 sm:max-w-md"
            />
          </div>
        </div>
      </section>

      {/* 2. About */}
      <section className="mx-auto max-w-site px-4 py-14 sm:py-16">
        <Reveal variant="morph">
          <div className="grid overflow-hidden rounded-[1.75rem] border border-border bg-card shadow-sm md:grid-cols-2">
            {/* Left Column: Eyebrow, Heading, Tagline & Feature Cards */}
            <div className="flex flex-col justify-center gap-6 p-8 sm:p-10 lg:p-12">
              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
                  {t("programs.gaushala.about.eyebrow")}
                </p>
                <h2 className="font-serif text-3xl font-semibold text-foreground sm:text-4xl">
                  {t("programs.gaushala.about.heading")}
                </h2>
                <p className="text-base text-muted-foreground">
                  {t("programs.gaushala.about.tagline")}
                </p>
              </div>

              {/* Feature Card below title */}
              <div className="border-t border-border/70 pt-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-primary">
                    <Home className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">
                      {t("programs.gaushala.about.capacityTitle")}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t("programs.gaushala.about.capacityDesc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Detailed Body Text */}
            <div className="flex flex-col justify-center border-t border-border/70 p-8 sm:p-10 md:border-l md:border-t-0 lg:p-12">
              <div className="space-y-3.5 text-[0.95rem] leading-relaxed text-muted-foreground">
                {t("programs.gaushala.about.body")
                  .split("\n\n")
                  .map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* 3. Our Impact */}
      <section className="border-border/40 bg-secondary/40 border-y py-14 sm:py-16">
        <div className="mx-auto max-w-site px-4">
          <Reveal className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
              {t("programs.gaushala.stats.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
              {t("programs.gaushala.stats.heading")}
            </h2>
          </Reveal>

          <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                value: impactStats.cowsSheltered,
                suffix: "+",
                labelKey: "programs.gaushala.stats.cowsSheltered",
                icon: Home,
              },
              {
                value: impactStats.dailyMeals,
                suffix: "+",
                labelKey: "programs.gaushala.stats.dailyMeals",
                icon: Sprout,
              },
              {
                value: impactStats.medicalCarePercentage,
                suffix: "%",
                labelKey: "programs.gaushala.stats.medicalCare",
                icon: Stethoscope,
              },
              {
                value: impactStats.yearsOfService,
                suffix: "+",
                labelKey: "programs.gaushala.stats.yearsOfService",
                icon: ShieldCheck,
              },
            ].map((stat, i) => {
              const StatIcon = stat.icon;
              return (
                <StaggerItem key={stat.labelKey} index={i} from="zoom">
                  <div className="flex items-center gap-4 rounded-2xl border border-border bg-card p-6 shadow-sm">
                    <div className="bg-primary/10 grid h-12 w-12 shrink-0 place-items-center rounded-xl text-primary">
                      <StatIcon className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="font-serif text-3xl font-bold text-foreground">
                        <CountUp to={stat.value} suffix={stat.suffix} />
                      </div>
                      <div className="mt-1 text-xs font-medium text-muted-foreground">
                        {t(stat.labelKey)}
                      </div>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* 4. Sponsorship Plans */}
      <section
        id="gaushala-sponsorships"
        className="mx-auto max-w-site px-4 py-14 sm:py-16"
      >
        <Reveal variant="fade">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#E38018]">
              SPONSORSHIP PROGRAMS
            </p>
            <h2 className="mt-3 font-serif text-3xl font-bold text-foreground sm:text-4xl md:text-5xl">
              <RevealText delay={100}>Choose Your Sponsorship Plan</RevealText>
            </h2>
            <p className="mt-3 text-sm text-muted-foreground sm:text-base">
              Support fodder, medical care, and gaushala development through
              transparent programs.
            </p>
          </div>
        </Reveal>

        {/* Dynamic Fodder Cards */}
        <Stagger
          className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          step={130}
        >
          {mainPrograms.map((prog, index) => (
            <StaggerItem key={prog.id} index={index} className="h-full">
              <SponsorshipPlanCard
                program={prog}
                sponsorLabel="SPONSOR NOW"
                onSponsor={() => openSponsorship(prog)}
              />
            </StaggerItem>
          ))}
        </Stagger>

        {/* Landscape Banner Card for Development Contribution */}
        {infrastructureProgram && (
          <div className="mt-10">
            <Reveal variant="wipe-up">
              <InfrastructureBannerCard
                program={infrastructureProgram}
                sponsorLabel="SPONSOR NOW"
                onSponsor={() => openSponsorship(infrastructureProgram)}
              />
            </Reveal>
          </div>
        )}
      </section>

      {/* 5. Why Support Us */}
      <section className="border-border/40 bg-secondary/30 border-y py-14 sm:py-16">
        <div className="mx-auto max-w-site px-4">
          <Reveal className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-accent">
              {t("programs.gaushala.why.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
              {t("programs.gaushala.why.heading")}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              {t("programs.gaushala.why.subtitle")}
            </p>
          </Reveal>

          <Stagger className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {whyUsCards.map((card, index) => {
              const IconComp = card.icon;
              return (
                <StaggerItem
                  key={card.titleKey}
                  index={index}
                  from={
                    index % 3 === 0 ? "left" : index % 3 === 2 ? "right" : "up"
                  }
                  className="h-full"
                >
                  <div
                    className={`flex h-full flex-col rounded-2xl p-6 ${cardHoverClass}`}
                  >
                    <div className="bg-primary/10 grid h-12 w-12 place-items-center rounded-xl text-primary">
                      <IconComp className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 font-serif text-xl font-semibold text-foreground">
                      {t(card.titleKey)}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {t(card.descKey)}
                    </p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* 6. Gallery */}
      <section className="mx-auto max-w-site px-4 py-14 sm:py-16">
        <Reveal className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
            {t("programs.gaushala.gallery.eyebrow")}
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
            {t("programs.gaushala.gallery.heading")}
          </h2>
        </Reveal>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {(dynamicGallery.length > 0
            ? dynamicGallery
            : galleryItems
          ).slice(0, 3).map((g: any, i) => {
            const title = g.titleKey ? t(g.titleKey) : g.title;
            const desc = g.descKey ? t(g.descKey) : g.desc;
            return (
              <Reveal key={title + i} variant="fade" delay={i * 120}>
                <figure className="card-lift group relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                  <ImageReveal
                    direction={i === 0 ? "left" : i === 1 ? "up" : "right"}
                    delay={i * 120}
                  >
                    <img
                      src={g.src}
                      alt={title}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </ImageReveal>
                  <div className="from-primary/90 via-primary/30 pointer-events-none absolute inset-0 bg-gradient-to-t to-transparent opacity-80 transition-opacity group-hover:opacity-95" />
                  <figcaption className="absolute inset-x-0 bottom-0 p-5 text-white">
                    <h3 className="font-serif text-lg font-semibold">{title}</h3>
                    {desc && <p className="mt-1 text-xs text-white/80">{desc}</p>}
                  </figcaption>
                </figure>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-8 text-center">
          <Button
            asChild
            variant="outline"
            className="border-primary/30 btn-lift rounded-full text-primary hover:bg-primary hover:text-primary-foreground"
          >
            <Link to="/gallery">
              View Full Gallery <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* 7. Bank & UPI Details */}
      <GaushalaBankDetails />

      {/* Sponsorship Modal */}
      <GaushalaSponsorshipModal
        open={modalOpen}
        selection={selection}
        onOpenChange={open => {
          setModalOpen(open);
          if (!open) {
            setSelection(null);
          }
        }}
      />
    </PageShell>
  );
}
